/**
 * Admin → Security → [EMAIL DELIVERY] panel
 * ─────────────────────────────────────────
 * Extracted from AdminSecurity.jsx to keep that page < 700 lines.
 *
 * Renders:
 *  - Transport status (primary, resend/smtp configured, from address)
 *  - One-click "SEND TEST EMAIL" probe
 *  - Verbatim JSON response of the last test send
 *  - Rolling ring-buffer of the last 5 send attempts
 *
 * Uses existing endpoints:
 *  - GET  /api/admin/email/health
 *  - POST /api/admin/email/test-send
 *
 * Both require system_admin | holding_admin (route + backend guarded).
 */
import React, { useEffect, useState } from 'react';
import { Mail, RefreshCw, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function EmailDeliveryPanel({ isDark }) {
  const [email, setEmail] = useState(null); // /admin/email/health snapshot
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testTo, setTestTo] = useState(''); // blank = signed-in email

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/email/health');
      setEmail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const body = testTo.trim()
        ? { to: testTo.trim(), purpose: 'diagnostic' }
        : { purpose: 'diagnostic' };
      const { data } = await api.post('/admin/email/test-send', body);
      setTestResult(data);
      if (data.ok) toast.success(`Diagnostic email dispatched via ${data.transport} → ${data.to}`);
      else toast.error(`Delivery failed via ${data.transport_attempted || 'transport'}`);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Test send failed';
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const primary = email?.transport?.primary;

  return (
    <div className="pal-card pal-card-live pal-grid-bg p-5" data-testid="admin-security-email-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail size={13} className="text-[var(--ve-violet-bright)]" />
          <span className="pal-bracket">[EMAIL DELIVERY]</span>
        </div>
        <div className="flex items-center gap-2">
          {primary && (
            <span className={`pal-status ${
              primary === 'none' ? 'pal-status-alert'
              : primary === 'resend' ? 'pal-status-violet'
              : 'pal-status-ok'
            }`}>
              {primary.toUpperCase()}
            </span>
          )}
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="admin-security-email-refresh"
            className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold ${
              isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            } disabled:opacity-50 flex items-center gap-1.5`}
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            REFRESH
          </button>
        </div>
      </div>

      <p className="text-xs mb-4 opacity-70 leading-relaxed">
        Verifies transactional email delivery (password resets, magic links, geo alerts, welcome emails).
        Fire a test send below to prove the transport is actually reaching the recipient — not just that config is present.
      </p>

      {!email ? (
        <div className="text-sm opacity-60">Loading…</div>
      ) : (
        <>
          {/* Provider readout row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Readout label="PRIMARY"
              value={(primary || 'none').toUpperCase()}
              accent={primary === 'none' ? 'rose' : 'emerald'}
              isDark={isDark} />
            <Readout label="RESEND"
              value={email.transport?.resend_configured ? 'CONFIGURED' : 'MISSING'}
              accent={email.transport?.resend_configured ? 'emerald' : 'rose'}
              isDark={isDark} />
            <Readout label="SMTP"
              value={email.transport?.smtp_configured ? 'CONFIGURED' : 'MISSING'}
              accent={email.transport?.smtp_configured ? 'emerald' : 'rose'}
              isDark={isDark} />
            <Readout label="FROM"
              value={email.transport?.resend_from || email.config?.from_email || '—'}
              accent="slate" isDark={isDark} />
          </div>

          {/* Test-send form */}
          <div className={`rounded border p-3 mb-4 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-slate-200 bg-slate-50'}`}>
            <div className="pal-bracket text-[9px] mb-2">[TEST · ONE-CLICK DELIVERY PROBE]</div>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1 min-w-0">
                <label className="pal-mono text-[9px] uppercase tracking-[0.20em] block mb-1 opacity-70">
                  RECIPIENT (BLANK = SIGNED-IN EMAIL)
                </label>
                <input
                  type="email"
                  value={testTo}
                  placeholder="you@example.com"
                  onChange={(e) => setTestTo(e.target.value)}
                  data-testid="admin-security-email-test-recipient"
                  className={`w-full px-3 py-2 text-sm rounded border bg-transparent pal-mono ${
                    isDark
                      ? 'border-white/10 text-white placeholder-slate-500 focus:border-[var(--ve-violet-bright)]'
                      : 'border-slate-300 text-[#0A0A0A] placeholder-slate-400 focus:border-[var(--ve-violet-shadow)]'
                  } focus:outline-none`}
                />
              </div>
              <button
                onClick={sendTest}
                disabled={testing || primary === 'none'}
                data-testid="admin-security-email-send-test"
                data-sending={testing ? 'true' : 'false'}
                className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded flex items-center gap-2 pal-mono transition-transform hover:translate-y-[-1px] ${
                  isDark ? 'bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)]' : 'bg-[var(--ve-violet-shadow)] text-white hover:bg-[var(--ve-violet)]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {testing ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                {testing ? 'SENDING…' : 'SEND TEST EMAIL →'}
              </button>
            </div>
            {primary === 'none' && (
              <div className={`mt-3 p-2.5 rounded border flex items-start gap-2 ${
                isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}>
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <div className="text-[11px] leading-relaxed pal-mono">
                  No transport configured. Set <code>RESEND_API_KEY</code> or <code>SMTP_HOST</code> in the deployment env vars.
                </div>
              </div>
            )}
          </div>

          {/* Verbatim last-send response */}
          {testResult && (
            <div
              data-testid="admin-security-email-test-result"
              className={`rounded border p-3 mb-4 ${
                testResult.ok
                  ? (isDark ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800')
                  : (isDark ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'       : 'bg-rose-50    border-rose-200    text-rose-800')
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {testResult.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
                <span className="pal-bracket text-[9px]">
                  [{testResult.ok ? 'DELIVERED' : 'FAILED'} · VERBATIM RESPONSE]
                </span>
              </div>
              <pre className="pal-mono text-[10.5px] leading-snug overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </div>
          )}

          {/* Recent attempts ring-buffer (last 5) */}
          {Array.isArray(email.recent_attempts) && email.recent_attempts.length > 0 && (
            <div>
              <div className="pal-bracket text-[9px] mb-2">[RECENT · LAST 5]</div>
              <div className={`rounded border divide-y ${isDark ? 'border-white/10 bg-white/[0.02] divide-white/[0.06]' : 'border-slate-200 bg-slate-50 divide-slate-200'}`}>
                {email.recent_attempts.slice(0, 5).map((a, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 pal-mono text-[10.5px] gap-3" data-testid={`admin-security-email-attempt-${i}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`pal-status shrink-0 ${a.ok ? 'pal-status-ok' : 'pal-status-alert'}`}>
                        {a.ok ? 'OK' : 'FAIL'}
                      </span>
                      <span className={`truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {a.purpose} → {a.to_domain}
                      </span>
                    </div>
                    <span className={`tabular-nums shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {a.ts ? new Date(a.ts).toLocaleTimeString() : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Tiny local readout (mirrors DataReadout in AdminSecurity but self-contained
 *   so this panel can be moved anywhere without cross-file coupling). ────── */
function Readout({ label, value, accent, isDark }) {
  const accents = {
    emerald: isDark
      ? { fg: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30' }
      : { fg: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
    rose: isDark
      ? { fg: 'text-rose-300',    bg: 'bg-rose-500/10 border-rose-500/30' }
      : { fg: 'text-rose-700',    bg: 'bg-rose-50 border-rose-200' },
    slate: isDark
      ? { fg: 'text-slate-200',   bg: 'bg-white/[0.03] border-white/10' }
      : { fg: 'text-slate-700',   bg: 'bg-slate-50 border-slate-200' },
  };
  const a = accents[accent] || accents.slate;
  return (
    <div className={`p-3 rounded-md border ${a.bg}`}>
      <div className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 mb-1">{label}</div>
      <div className={`pal-mono text-sm tabular-nums font-bold ${a.fg} break-all`}>{value}</div>
    </div>
  );
}
